<template>
  <div class="stream-group-row d-flex justify-between items-center">
    <button
      class="move-detail-button-area d-flex justify-between items-center"
      @click="moveStreamGroupDetail(streamGroup.streamGroupId)"
    >
      <div class="stream-group-icon-wrapper">
        <img
          :src="StreamGroupIcon"
          alt="stream group icon"
          :class="['stream-group-icon', { 'is-active': isActiveStreamGroupIcon }]"
        />
        <span :class="['badge', { 'is-active-badge': isActiveStreamGroupIcon }]">{{
          streamGroup.contentCount
        }}</span>
      </div>
      <div class="stream-group-info">
        <div class="stream-group-title">
          {{ streamGroup.streamGroupName }}
        </div>
        <div class="stream-group-detail d-flex justify-between items-center">
          <div class="stream-group-duration d-flex justify-between items-center">
            {{ convertSecToms(streamGroup.streamGroupDuration) }}
          </div>
        </div>
        <div class="stream-group-schedule-info">
          <div
            :class="[
              'stream-group-schedule-info-detail',
              { 'is-active-schedule-info-detail': !isActiveStreamGroupIcon },
            ]"
          >
            <div class="stream-time">{{ streamingStartTime }} - {{ streamingEndTime }}に配信</div>
            <div class="day-of-week-wrapper">
              <div
                v-for="dayOfWeek in DAY_OF_WEEK_LIST"
                :key="dayOfWeek"
                :class="[
                  'day-of-week',
                  { 'is-active': streamGroup.dayOfWeekList.includes(dayOfWeek) },
                ]"
              >
                {{ dayOfWeek }}
              </div>
            </div>
          </div>
          <ja-stream-group-upload-status-badge :streamGroup="streamGroup" />
        </div>
      </div>
    </button>
    <div class="schedule-cancel-button-area">
      <button
        v-if="
          streamGroup.streamGroupUploadStatus === STREAM_GROUP_UPLOAD_STATUS.STREAM_GROUP_UPLOADING
        "
        :disabled="true"
        class="steaming-registration-button schedule-cancel-button:disabled"
      >
        登録中
      </button>
      <button
        v-else
        @click="
          onClickScheduleCancelButton(streamGroup.streamingScheduleId, streamGroup.uploadStatus)
        "
        :disabled="isDisabledScheduleCancelButton"
        class="schedule-cancel-button"
      >
        解除
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import JaStreamGroupUploadStatusBadge from '@/components/JaStreamGroup/JaStreamGroupUploadStatusBadge.vue';
import StreamGroupIcon from '@/assets/images/StreamGroupButton.png';

import { UPLOAD_STATUS_ID_LIST, STREAM_GROUP_UPLOAD_STATUS } from '@/composable/constant_common';
import { putStreamingScheduleUploadStatus } from '@/composable/data_push_common';
import { convertSecToms } from '@/composable/material_common';

const props = defineProps({
  isStreaming: Boolean,
  streamGroup: Object,
  setIsAxiosWait: Function,
  getStreamGroup: Function,
  isAxiosWait: Boolean,
  openModal: Function,
  moveStreamGroupDetail: Function,
});

// 配信グループ再生秒数アイコンファイル名
const DAY_OF_WEEK_LIST = ['月', '火', '水', '木', '金', '土', '日'];

// 時刻表示
const streamingStartTime = computed(
  () =>
    `${`0${props.streamGroup.streamingStartHour}`.slice(
      -2
    )}:${`0${props.streamGroup.streamingStartMinute}`.slice(-2)}`
);
const streamingEndTime = computed(
  () =>
    `${`0${props.streamGroup.streamingEndHour}`.slice(
      -2
    )}:${`0${props.streamGroup.streamingEndMinute}`.slice(-2)}`
);

// アクティブ判定
const isActiveStreamGroupIcon = computed(() => {
  const IS_ACTIVE_UPLOAD_STATUSES_ID = [
    UPLOAD_STATUS_ID_LIST.SCHEDULED,
    UPLOAD_STATUS_ID_LIST.UPLOADING,
  ];
  return IS_ACTIVE_UPLOAD_STATUSES_ID.includes(props.streamGroup.uploadStatusId);
});

// キャンセルボタン非活性判定
const isDisabledScheduleCancelButton = computed(
  () => props.isAxiosWait || props.streamGroup.uploadStatusId === UPLOAD_STATUS_ID_LIST.CANCEL
);

/**
 * アップロードステータス更新処理
 */
const onClickScheduleCancelButton = (streamingScheduleId, uploadStatusId) => {
  // 通信処理中の場合は処理中止
  if (props.isAxiosWait) return;
  if (uploadStatusId === UPLOAD_STATUS_ID_LIST.CANCEL) return;

  const sendUploadStatusId = UPLOAD_STATUS_ID_LIST.CANCEL;
  // ボタン連打回避のためフラグ設定
  props.setIsAxiosWait(true);
  putStreamingScheduleUploadStatus(streamingScheduleId, sendUploadStatusId)
    .then(() => {
      // 配信ステータス更新成功時、親コンポーネントの配信グループ一覧取得を実行
      props.getStreamGroup();
    })
    .finally(() => {
      // ボタン連打回避のためフラグ設定
      props.setIsAxiosWait(false);
    });
};
</script>

<style scoped>
/** 配信グループ */
.stream-group-row {
  position: relative;
  box-sizing: border-box;
  border-bottom: 0.1rem solid #ddd;
  width: 100%;
}
.stream-group-row:first-child {
  border-top: 0.1rem solid #ddd;
}

/** 詳細画面遷移ボタン */
.move-detail-button-area {
  width: 75%;
  padding-block: 2rem;
  background-color: white;
  border: none;
}

/** アップロードステータスアイコンエリア */
.stream-group-icon-wrapper {
  height: 4rem;
  width: 4rem;
  padding: 2rem;
  position: relative;
  flex-shrink: 0;
}
.stream-group-icon {
  height: 100%;
  width: 100%;
  object-fit: contain;
  filter: gray;
  -webkit-filter: grayscale(1);
}
.is-active.stream-group-icon {
  /** 予約完了している場合にはグレースケールフィルターを外す */
  filter: none;
}
/** コンテンツ数バッチアイコン */

.badge.is-active-badge {
  /** アップロード完了している場合にはグレースケールフィルターを外す */
  background-color: #d8f3de;
  color: #21963b;
}
.badge {
  background-color: rgba(170, 170, 170, 0.87);
  color: white;
  font-size: 1.1rem;
  padding: 0.3rem;
  width: 1.3rem;
  height: 1.3rem;
  border-radius: 50%;
  position: absolute;
  right: 1.1rem;
  top: 1.5rem;
  z-index: 1;
}

/** 配信グループ情報表示エリア */
.stream-group-info {
  flex-grow: 3;
}
.stream-group-title {
  font-size: 1.6rem;
  margin-bottom: 0.5rem;
  text-align: left;
  color: #222222;
}
.stream-group-detail {
  margin-bottom: 0.5rem;
  font-size: 1.4rem;
  color: #aaaaaa;
}
.stream-group-duration-icon {
  margin-right: 0.5rem;
}

/** 配信予約情報エリア */
.stream-group-schedule-info {
  font-size: 1.4rem;
  text-align: left;
  color: #444;
}
.stream-group-schedule-info-detail {
  width: 100%;
  box-sizing: border-box;
}
.day-of-week-wrapper {
  display: flex;
  padding-block: 1rem;
  gap: 0.5rem;
}
.day-of-week {
  color: #aaaaaa;
  border: solid 1px #dddddd;
  background-color: #ffffff;
  font-size: 1.1rem;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  font-weight: 400;
  text-align: center;
  line-height: 2rem;
  flex-shrink: 0;
}
.day-of-week.is-active {
  color: #ffffff;
  background-color: #21963b;
  border: solid 1px #21963b;
  font-weight: 700;
}
.is-active-schedule-info-detail .day-of-week.is-active {
  background-color: #aaaaaa;
  border: solid 1px #aaaaaa;
}
.stream-time {
  color: #21963b;
  font-weight: 700;
  font-size: 1.6rem;
}
.is-active-schedule-info-detail .stream-time {
  color: #aaaaaa;
}

/** 予約キャンセルボタン */
.schedule-cancel-button-area {
  padding: 1.5rem;
}
.schedule-cancel-button {
  font-size: 1.4rem;
  padding: 0.5rem 1rem;
  background-color: white;
  border: 1px #cccccc solid;
  border-radius: 0.5rem;
  color: #e63030;
}
.steaming-registration-button {
  font-size: 1.4rem;
  padding: 0.5rem 1rem;
  background-color: white;
  border: 1px #cccccc solid;
  border-radius: 0.5rem;
}
.schedule-cancel-button:disabled {
  color: #9e9e9e;
}
</style>
